import { useAnalytics, getAuthEntryPoint, type AuthMethod } from '@shared/lib'

// Источник открытия входа живёт в shared/lib/analytics: модалку открывают корзина,
// профиль, подарки, диплинки — фичи друг друга не импортируют. Реэкспорт — для
// существующих импортов из features/auth.
export { setAuthEntryPoint } from '@shared/lib'

/**
 * Воронка авторизации: показали способы → нажали → ушли в сервис → вернулись → вошли.
 *
 * Способ входа приходится помнить между шагами: успех у всех шести входов сходится
 * в одну точку (`useLogoutHandler.handleSuccessfulLogin`), и там уже неизвестно,
 * через что именно вошли. Поэтому метод фиксируется на клике и живёт до успеха.
 */
let pendingMethod: AuthMethod | null = null
let pendingSource: string | undefined
let startedAt = 0

/** Дольше этого попытку входа считаем брошенной: пользователь ушёл в сервис и не вернулся. */
const ATTEMPT_TTL_MS = 15 * 60 * 1000

const isAttemptAlive = (): boolean => pendingMethod !== null && Date.now() - startedAt < ATTEMPT_TTL_MS

/**
 * Список способов доезжает частями (настройки заведения и гейт версии приходят
 * асинхронно), и watch в модалке срабатывал на каждое пополнение — за одно открытие
 * уезжало несколько auth_methods_shown. Ждём, пока список устаканится, и шлём один раз.
 */
const METHODS_SETTLE_MS = 600
let methodsShownTimer: ReturnType<typeof setTimeout> | null = null

export const useAuthAnalytics = () => {
  const { track } = useAnalytics()

  /** Модалка входа открыта. Источник отвечает на «что заставило» — корзина, профиль, пуш. */
  const trackAuthModalOpen = (source?: string): void => {
    track('auth_modal_open', { source: source ?? getAuthEntryPoint() })
  }

  /**
   * Какие способы реально показали.
   *
   * Без этого шага конверсия по способу считается от неверной базы: список режется и
   * настройками заведения (VK включается строкой institution_vk), и гейтом по версии
   * сборки — у части устройств кнопки просто нет.
   */
  const trackMethodsShown = (methods: AuthMethod[]): void => {
    if (methods.length === 0) return

    const settled = [...methods]
    if (methodsShownTimer) clearTimeout(methodsShownTimer)
    methodsShownTimer = setTimeout(() => {
      methodsShownTimer = null
      track('auth_methods_shown', { methods: settled })
    }, METHODS_SETTLE_MS)
  }

  /** Нажали кнопку способа. Отсюда начинается отсчёт попытки. */
  const trackMethodClick = (method: AuthMethod, source?: string): void => {
    // Повтор того же способа внутри живой попытки — это её продолжение (обмен токена
    // после нативной кнопки VK), а не второй клик: попытку продлеваем, событие не дублируем.
    const isSameAttempt = isAttemptAlive() && pendingMethod === method

    pendingMethod = method
    pendingSource = source ?? pendingSource
    startedAt = Date.now()

    if (!isSameAttempt) track('auth_method_click', { method, source })
  }

  /** Уходим во внешнее приложение или браузер — дальше клиент нас не видит. */
  const trackRedirectStart = (method: AuthMethod): void => {
    track('auth_redirect_start', { method })
  }

  /**
   * Вернулись из сервиса. Отделяет отказ на стороне сервиса от нашей ошибки обмена:
   * шаг «сервис ответил» виден клиенту, а сам обмен токенов — только бэку.
   */
  const trackCallback = (method: AuthMethod, ok: boolean, errorCode?: string): void => {
    track('auth_callback', { method, ok, errorCode })
  }

  /**
   * Вход состоялся. Вызывается из общей точки успеха, поэтому способ берётся из
   * запомненной попытки; без неё (восстановление сессии, автологин) событие не шлём —
   * иначе воронка будет считать входами продления сессии.
   */
  const trackAuthSuccess = (isNewUser?: boolean): void => {
    if (!isAttemptAlive()) {
      pendingMethod = null
      return
    }

    track('auth_success', { method: pendingMethod!, isNewUser })

    pendingMethod = null
    pendingSource = undefined
  }

  const getPendingMethod = (): AuthMethod | null => (isAttemptAlive() ? pendingMethod : null)

  const getPendingSource = (): string | undefined => pendingSource

  return {
    trackAuthModalOpen,
    trackMethodsShown,
    trackMethodClick,
    trackRedirectStart,
    trackCallback,
    trackAuthSuccess,
    getPendingMethod,
    getPendingSource,
  }
}
